/**
 * Choosing the machine type a host starts on, and reporting a start that failed.
 *
 * Both live here because they are the same control in two moods. A stockout is
 * the reason the choice exists, so the error and the menu have to appear
 * together — an operator who reads "no c4-standard-16 capacity" and then has to
 * find a control elsewhere is being told about a problem rather than offered the
 * way past it. The menu is also available before anything goes wrong, because
 * Pulumi no longer reverts the shape (`machineTypeChoosable` in
 * `factory-hosts/loops.ts`) and this is now the way back to the usual one.
 *
 * Only a host with `machineTypeChoices` gets any of this; for the prepare host
 * these render as a plain button and a plain alert.
 */
import { useRef, useState } from 'react';
import {
    Alert,
    AlertTitle,
    Button,
    type ButtonProps,
    ButtonGroup,
    CircularProgress,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
    describeMachineTypeChoice,
    hostInstance,
    type HostRole,
    type MachineTypeChoice,
} from '@finvari/deployer';
import type { StartFailure } from '../lib/hostPower';

/** What a menu row asks for: one named shape, or a walk down the whole list. */
export type MachineTypeRequest = { kind: 'one'; machineType: string } | { kind: 'all' };

interface MachineTypeMenuProps {
    role: HostRole;
    onChoose: (request: MachineTypeRequest) => void;
    disabled?: boolean;
    /** Shapes a start just failed on, shown struck through so a retry is informed. */
    tried?: string[];
    /** The face of the dropdown half; a lone icon when the menu stands beside a button. */
    label?: string;
    /** Inside a `ButtonGroup` these must match the group's: a button's own props override the group's. */
    variant?: ButtonProps['variant'];
    size?: ButtonProps['size'];
}

/** The host's choices, or undefined when it has none to offer. */
export function machineTypeChoicesFor(role: HostRole): readonly MachineTypeChoice[] | undefined {
    const choices = hostInstance(role)?.machineTypeChoices;
    return choices?.length ? choices : undefined;
}

export function MachineTypeMenu({ role, onChoose, disabled, tried = [], label, variant = 'outlined', size = 'small' }: MachineTypeMenuProps) {
    const anchor = useRef<HTMLButtonElement | null>(null);
    const [open, setOpen] = useState(false);
    const choices = machineTypeChoicesFor(role);
    if (!choices) return null;

    const choose = (request: MachineTypeRequest) => {
        setOpen(false);
        onChoose(request);
    };

    return (
        <>
            <Button
                ref={anchor}
                variant={variant}
                size={size}
                disabled={disabled}
                onClick={() => setOpen(true)}
                endIcon={<ArrowDropDownIcon />}
            >
                {label ?? 'Start on…'}
            </Button>
            <Menu anchorEl={anchor.current} open={open} onClose={() => setOpen(false)}>
                {/*
                  * First, because it is the right answer to a stockout nearly every
                  * time: the operator wants the release out, not a particular shape.
                  */}
                <MenuItem onClick={() => choose({ kind: 'all' })}>
                    <ListItemText
                        primary="Try each until one starts"
                        secondary="Works down the list below, stopping at the first with capacity"
                    />
                </MenuItem>
                {choices.map((choice) => {
                    const failed = tried.includes(choice.machineType);
                    return (
                        <MenuItem
                            key={choice.machineType}
                            onClick={() => choose({ kind: 'one', machineType: choice.machineType })}
                        >
                            <ListItemText
                                primary={choice.machineType}
                                secondary={
                                    failed
                                        ? `Out of stock just now · ${describeMachineTypeChoice(choice)}`
                                        : describeMachineTypeChoice(choice)
                                }
                                sx={failed ? { opacity: 0.6 } : undefined}
                            />
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
}

interface StartFailureAlertProps {
    role: HostRole;
    failure: StartFailure;
    title: string;
    onChoose: (request: MachineTypeRequest) => void;
    sending?: boolean;
}

/**
 * A start that did not happen, with the way past it when there is one.
 *
 * Shared by the host card and the release flow's wake panel so the two cannot
 * describe the same Compute failure differently — they are both reporting one
 * callable, and an operator who sees it in one place and then the other should
 * not have to work out whether they are the same problem.
 */
export function StartFailureAlert({ role, failure, title, onChoose, sending }: StartFailureAlertProps) {
    const stockout = failure.stockout;
    return (
        <Alert severity={stockout ? 'warning' : 'error'}>
            <AlertTitle>{title}</AlertTitle>
            <Stack spacing={1} alignItems="flex-start">
                <Typography variant="body2">{failure.message}</Typography>
                {stockout && machineTypeChoicesFor(role) && (
                    <MachineTypeMenu
                        role={role}
                        onChoose={onChoose}
                        disabled={sending}
                        tried={stockout.tried}
                        label="Start on another machine type…"
                    />
                )}
            </Stack>
        </Alert>
    );
}

interface StartHostControlProps {
    role: HostRole;
    /** `undefined` starts the VM on whatever shape it is already on. */
    onStart: (request?: MachineTypeRequest) => void;
    sending: boolean;
    disabled?: boolean;
    label: string;
    /** The shape the plain Start will keep, from the host's own heartbeat. */
    currentMachineType?: string;
    size?: 'small' | 'medium';
}

/**
 * Start, plus the menu when the host has one. A split button rather than two
 * buttons: starting on the current shape is overwhelmingly the common case and
 * should stay one press, while the choice is one press away rather than hidden
 * behind a failure.
 */
export function StartHostControl({
    role,
    onStart,
    sending,
    disabled,
    label,
    currentMachineType,
    size = 'medium',
}: StartHostControlProps) {
    const choices = machineTypeChoicesFor(role);
    const start = (
        <Button
            variant="contained"
            size={size}
            onClick={() => onStart()}
            disabled={sending || disabled}
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
            {sending ? 'Starting VM…' : currentMachineType ? `${label} (${currentMachineType})` : label}
        </Button>
    );

    if (!choices) return start;

    return (
        <ButtonGroup variant="contained" size={size} disabled={sending || disabled}>
            {start}
            <MachineTypeMenu
                role={role}
                onChoose={(request) => onStart(request)}
                disabled={sending || disabled}
                label=""
                variant="contained"
                size={size}
            />
        </ButtonGroup>
    );
}
